/**
 * WP-50 — number formats (plan 8.1 "number formats").
 *
 * A deliberately small in-house reader of the numfmt string the xlsx reader
 * stored on the cell (plan 5.5 cta_cells.numfmt). It covers currency, percent,
 * thousands separators, a fixed number of decimals, and date serials
 * (date1904 false, which is what this corpus uses). Anything else falls back
 * to the plain value.
 *
 * NEVER INVENT PRECISION (G7): the only digits shown past the point are the
 * ones the pattern itself asks for; with no pattern the value is printed at 15
 * significant digits — the most a double carries reliably, and Excel's own
 * stored precision — so binary noise (0.1 + 0.2 = 0.30000000000000004) is
 * stripped without inventing a digit the value never had.
 */
import { EngineValue, isErrorBox } from './engineBridge';

export interface FormattedValue {
  text: string;
  align: 'left' | 'right';
  /** What the format reader actually applied, for the hover text. */
  applied: 'general' | 'number' | 'currency' | 'percent' | 'date' | 'text' | 'error' | 'boolean';
}

const EMPTY: FormattedValue = { text: '', align: 'left', applied: 'general' };

/** Drop the parts of a pattern that are not placeholders: quoted runs, escapes, [..] sections. */
function placeholderPart(pattern: string): string {
  const noQuotes = pattern.replace(/"[^"]*"/g, '').replace(/\\./g, '');
  const noBrackets = noQuotes.replace(/\[[^\]]*\]/g, '');
  // Excel patterns are positive;negative;zero;text — the positive section rules.
  return noBrackets.split(';')[0];
}

function looksLikeDate(pattern: string): boolean {
  const p = placeholderPart(pattern);
  if (/[#0]/.test(p)) return false;
  return /(?:y{2,4}|d{1,4}|m{1,5}|h{1,2}|s{1,2})/i.test(p);
}

function decimalsOf(pattern: string): number {
  const m = /\.(0+)/.exec(placeholderPart(pattern));
  return m ? m[1].length : 0;
}

/**
 * Excel serial -> ISO date. Day 1 is 1900-01-01 and Excel's 1900 leap-year bug
 * is preserved by anchoring at 1899-12-30, the same anchor the backend reader
 * uses. Rendered ISO rather than mimicking the sponsor's pattern, so a date is
 * never ambiguous between US and EU order (the corpus has both).
 */
export function serialToIsoDate(serial: number): string | null {
  if (!isFinite(serial) || serial < 1) return null;
  const ms = Date.UTC(1899, 11, 30) + Math.floor(serial) * 86400000;
  const d = new Date(ms);
  if (isNaN(d.getTime())) return null;
  const y = String(d.getUTCFullYear()).padStart(4, '0');
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return y + '-' + m + '-' + day;
}

function withThousands(intPart: string): string {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function fixed(n: number, decimals: number, thousands: boolean): string {
  const negative = n < 0;
  const abs = Math.abs(n);
  const body = decimals > 0 ? abs.toFixed(decimals) : String(Math.round(abs));
  const parts = body.split('.');
  const head = thousands ? withThousands(parts[0]) : parts[0];
  const text = parts.length > 1 ? head + '.' + parts[1] : head;
  return negative ? '-' + text : text;
}

/** The plain rendering of a number: no padding, no separators, no invented digits. */
export function plainNumber(n: number): string {
  if (!isFinite(n)) return String(n);
  return String(parseFloat(n.toPrecision(15)));
}

/**
 * The unformatted text of a value — what a cta_ws_changes row carries in
 * old_value / new_value (plan 5.8: those columns are text).
 */
export function valueToText(value: EngineValue | undefined): string | null {
  if (value === null || value === undefined) return null;
  if (isErrorBox(value)) return value.__error;
  if (typeof value === 'number') return plainNumber(value);
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
  const text = String(value);
  return text.length === 0 ? null : text;
}

/**
 * Format a value for display. `numfmt` and `isDate` come from the cell
 * (plan 5.5); a formula result is formatted with its own cell's numfmt, the
 * way Excel does.
 */
export function formatValue(
  value: EngineValue | undefined,
  numfmt: string | null | undefined,
  isDate: boolean | null | undefined,
): FormattedValue {
  if (value === null || value === undefined) return EMPTY;
  if (isErrorBox(value)) return { text: value.__error, align: 'right', applied: 'error' };
  if (typeof value === 'boolean') return { text: value ? 'TRUE' : 'FALSE', align: 'right', applied: 'boolean' };
  if (typeof value === 'string') {
    return { text: value, align: 'left', applied: 'text' };
  }

  const pattern = typeof numfmt === 'string' ? numfmt.trim() : '';
  const general = pattern.length === 0 || /^general$/i.test(pattern);

  if (isDate === true || (!general && looksLikeDate(pattern))) {
    const iso = serialToIsoDate(value);
    if (iso !== null) return { text: iso, align: 'right', applied: 'date' };
    // A date format over something that is not a serial: show the number
    // rather than a wrong date (G13 — never guess).
    return { text: plainNumber(value), align: 'right', applied: 'number' };
  }

  if (general) return { text: plainNumber(value), align: 'right', applied: 'general' };

  const body = placeholderPart(pattern);
  const decimals = decimalsOf(pattern);
  const thousands = body.indexOf('#,#') !== -1 || body.indexOf('0,0') !== -1;

  if (body.indexOf('%') !== -1) {
    return { text: fixed(value * 100, decimals, thousands) + '%', align: 'right', applied: 'percent' };
  }
  if (pattern.indexOf('$') !== -1) {
    const n = fixed(value, decimals, thousands);
    const text = n.charAt(0) === '-' ? '-$' + n.slice(1) : '$' + n;
    return { text: text, align: 'right', applied: 'currency' };
  }
  return { text: fixed(value, decimals, thousands), align: 'right', applied: 'number' };
}
